import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Package, Save, User as UserIcon, Wallet, X, FileText, Trash2, Plus, Clock, CalendarCheck, Briefcase, Shield, Phone, Link as LinkIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getUser, updateUser } from '@/api/users'
import { listInventory } from '@/api/inventory'
import AttendanceTimeline from '@/components/AttendanceTimeline'
import { getUserBalance, type LeaveBalance } from '@/api/leaves'
import { listDepartments, listDesignations } from '@/api/orgstructure'
import { listRoles, type RoleDef } from '@/api/roles'
import { getUserSalary, setUserSalary, money, type SalaryInput } from '@/api/payroll'
import { listUserDocuments, createDocument, deleteDocument, type DocType, type DocumentInput } from '@/api/documents'
import { useAuthStore } from '@/stores/authStore'
import StatTile from '@/components/dashboard/StatTile'
import { cn } from '@/lib/utils'
import type { UserUpdate } from '@/types'

type TabKey = 'employee' | 'employment' | 'salary' | 'user-info' | 'contact' | 'assets' | 'documents' | 'attendance' | 'leave'
const TABS: { key: TabKey; label: string; icon: typeof UserIcon }[] = [
  { key: 'employee', label: 'Employee Info', icon: UserIcon },
  { key: 'employment', label: 'Employment Info', icon: Briefcase },
  { key: 'salary', label: 'Salary & Benefits', icon: Wallet },
  { key: 'user-info', label: 'User Information', icon: Shield },
  { key: 'contact', label: 'Contact Info', icon: Phone },
  { key: 'assets', label: 'Assets', icon: Package },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'attendance', label: 'Attendance', icon: Clock },
  { key: 'leave', label: 'Leave & Policies', icon: CalendarCheck },
]
// Tabs whose fields belong to the single shared `form`/`handleSave` — the
// same UserUpdate payload as before, just shown one group at a time now.
const FORM_TABS: TabKey[] = ['employee', 'employment', 'contact']

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'
const ROLES = ['team_member', 'admin'] as const
const ROLE_LABELS: Record<string, string> = {
  team_member: 'Team Member', admin: 'Admin',
}
const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'intern'] as const
const EMPLOYMENT_STATUSES = ['active', 'on_notice', 'resigned', 'terminated'] as const
const GENDERS = ['male', 'female', 'other'] as const
const MARITAL_STATUSES = ['single', 'married', 'divorced', 'widowed'] as const

// Fields sent as null when blank
function orNull(v: string | undefined | null) {
  return v ? v : null
}

export default function UserDetail() {
  const { userId, orgSlug } = useParams<{ userId: string; orgSlug: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'

  const [form, setForm] = useState<UserUpdate>({})
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [tab, setTab] = useState<TabKey>('employee')

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId!),
    enabled: !!userId,
  })
  const { data: assigned } = useQuery({
    queryKey: ['inventory', 'assigned_to', user?.id],
    queryFn: () => listInventory({ assigned_to: user!.id, per_page: 100 }),
    enabled: !!user?.id,
  })
  const { data: departments = [] } = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const { data: designations = [] } = useQuery({ queryKey: ['designations'], queryFn: () => listDesignations() })
  const { data: customRoles = [] } = useQuery({ queryKey: ['roles'], queryFn: () => listRoles() })

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name,
        role: user.role,
        standup_time: user.standup_time,
        eod_time: user.eod_time,
        shift_days: user.shift_days,
        is_active: user.is_active,
        employee_id: user.employee_id || '',
        department_id: user.department_id || '',
        designation_id: user.designation_id || '',
        employment_type: user.employment_type || '',
        employment_status: user.employment_status || 'active',
        date_of_joining: user.date_of_joining || '',
        date_of_birth: user.date_of_birth || '',
        probation_end_date: user.probation_end_date || '',
        resignation_date: user.resignation_date || '',
        last_working_day: user.last_working_day || '',
        phone: user.phone || '',
        gender: user.gender || '',
        marital_status: user.marital_status || '',
        cnic: user.cnic || '',
        bank_code: user.bank_code || '',
        bank_account_number: user.bank_account_number || '',
        address: user.address || '',
        emergency_contact_name: user.emergency_contact_name || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
      })
    }
  }, [user])

  const saveMutation = useMutation({
    mutationFn: (payload: UserUpdate) => updateUser(user!.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', userId] })
      qc.invalidateQueries({ queryKey: ['users'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to save'),
  })

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    const payload: UserUpdate = {
      name: form.name,
      standup_time: orNull(form.standup_time),
      eod_time: orNull(form.eod_time),
      shift_days: form.shift_days || undefined,
      employee_id: orNull(form.employee_id),
      department_id: orNull(form.department_id),
      designation_id: orNull(form.designation_id),
      employment_type: orNull(form.employment_type) as UserUpdate['employment_type'],
      employment_status: orNull(form.employment_status) as UserUpdate['employment_status'],
      date_of_joining: orNull(form.date_of_joining),
      date_of_birth: orNull(form.date_of_birth),
      probation_end_date: orNull(form.probation_end_date),
      resignation_date: orNull(form.resignation_date),
      last_working_day: orNull(form.last_working_day),
      phone: orNull(form.phone),
      gender: orNull(form.gender),
      marital_status: orNull(form.marital_status),
      cnic: orNull(form.cnic),
      bank_code: orNull(form.bank_code),
      bank_account_number: orNull(form.bank_account_number),
      address: orNull(form.address),
      emergency_contact_name: orNull(form.emergency_contact_name),
      emergency_contact_phone: orNull(form.emergency_contact_phone),
    }
    if (isAdmin) {
      payload.role = form.role
      payload.is_active = form.is_active
    }
    saveMutation.mutate(payload)
  }

  if (isLoading || !user) {
    return <div className="max-w-4xl mx-auto text-gray-400">Loading...</div>
  }

  const items = assigned?.items ?? []
  const set = (patch: Partial<UserUpdate>) => setForm({ ...form, ...patch })

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate(`/${orgSlug}/admin/users`)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </button>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-full bg-[#EC5B2C]/10 flex items-center justify-center shrink-0">
            {user.avatar_url ? <img src={user.avatar_url} className="h-14 w-14 rounded-full" alt={user.name} /> : <UserIcon className="h-7 w-7 text-[#EC5B2C]" />}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900">{user.name}</h1>
              {user.employee_id && (
                <span className="rounded-full bg-[#EC5B2C]/10 px-2 py-0.5 text-xs font-semibold text-[#EC5B2C]">{user.employee_id}</span>
              )}
            </div>
            <p className="text-sm text-gray-500">{user.email}{user.slack_id ? ' · Slack linked' : ' · No Slack'}</p>
          </div>
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => set({ is_active: e.target.checked })} />
            Login Active
          </label>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'flex shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
              tab === t.key ? 'border-[#EC5B2C] text-[#EC5B2C]' : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {FORM_TABS.includes(tab) ? (
        <form onSubmit={handleSave} className="bg-white rounded-lg border border-gray-200 p-5 space-y-5">
          {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
          {saved && <div className="rounded bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2">Saved.</div>}

          {tab === 'employee' && (
            <Section title="Employee Info">
              <Grid>
                <F label="Full Name"><input className={inputCls} value={form.name || ''} onChange={(e) => set({ name: e.target.value })} /></F>
                <F label="Phone"><input className={inputCls} value={form.phone || ''} onChange={(e) => set({ phone: e.target.value })} /></F>
                <F label="Date of Birth"><input type="date" className={inputCls} value={form.date_of_birth || ''} onChange={(e) => set({ date_of_birth: e.target.value })} /></F>
                <F label="Gender">
                  <select className={`${inputCls} capitalize`} value={form.gender || ''} onChange={(e) => set({ gender: e.target.value })}>
                    <option value="">—</option>
                    {GENDERS.map((g) => <option key={g} value={g} className="capitalize">{g}</option>)}
                  </select>
                </F>
                <F label="Marital Status">
                  <select className={`${inputCls} capitalize`} value={form.marital_status || ''} onChange={(e) => set({ marital_status: e.target.value })}>
                    <option value="">—</option>
                    {MARITAL_STATUSES.map((m) => <option key={m} value={m} className="capitalize">{m}</option>)}
                  </select>
                </F>
                <F label="CNIC"><input className={inputCls} placeholder="00000-0000000-0" value={form.cnic || ''} onChange={(e) => set({ cnic: e.target.value })} /></F>
              </Grid>
              <Grid>
                <F label="Bank Code"><input className={inputCls} value={form.bank_code || ''} onChange={(e) => set({ bank_code: e.target.value })} /></F>
                <F label="Bank Account Number"><input className={inputCls} value={form.bank_account_number || ''} onChange={(e) => set({ bank_account_number: e.target.value })} /></F>
              </Grid>
            </Section>
          )}

          {tab === 'employment' && (
            <Section title="Employment Info">
              <Grid>
                <F label="Employee ID"><input className={inputCls} value={form.employee_id || ''} onChange={(e) => set({ employee_id: e.target.value })} /></F>
                <F label="Role" hint={!isAdmin ? 'admin only' : undefined}>
                  <select disabled={!isAdmin} value={form.role || user.role} onChange={(e) => set({ role: e.target.value as UserUpdate['role'] })} className={`${inputCls} disabled:bg-gray-100 disabled:text-gray-500`}>
                    <optgroup label="Built-in">
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>)}
                    </optgroup>
                    {customRoles.length > 0 && (
                      <optgroup label="Custom">
                        {customRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </optgroup>
                    )}
                  </select>
                </F>
                <F label="Department">
                  <select className={inputCls} value={form.department_id || ''} onChange={(e) => set({ department_id: e.target.value })}>
                    <option value="">—</option>
                    {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </F>
                <F label="Designation">
                  <select className={inputCls} value={form.designation_id || ''} onChange={(e) => set({ designation_id: e.target.value })}>
                    <option value="">—</option>
                    {designations.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                  </select>
                </F>
                <F label="Employment Type">
                  <select className={`${inputCls} capitalize`} value={form.employment_type || ''} onChange={(e) => set({ employment_type: e.target.value as UserUpdate['employment_type'] })}>
                    <option value="">—</option>
                    {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t.replace('_', ' ')}</option>)}
                  </select>
                </F>
                <F label="Employment Status">
                  <select className={`${inputCls} capitalize`} value={form.employment_status || 'active'} onChange={(e) => set({ employment_status: e.target.value as UserUpdate['employment_status'] })}>
                    {EMPLOYMENT_STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s.replace('_', ' ')}</option>)}
                  </select>
                </F>
                <F label="Date of Joining"><input type="date" className={inputCls} value={form.date_of_joining || ''} onChange={(e) => set({ date_of_joining: e.target.value })} /></F>
                <F label="Probation End Date"><input type="date" className={inputCls} value={form.probation_end_date || ''} onChange={(e) => set({ probation_end_date: e.target.value })} /></F>
                <F label="Resignation Date"><input type="date" className={inputCls} value={form.resignation_date || ''} onChange={(e) => set({ resignation_date: e.target.value })} /></F>
                <F label="Last Working Day"><input type="date" className={inputCls} value={form.last_working_day || ''} onChange={(e) => set({ last_working_day: e.target.value })} /></F>
              </Grid>
              <Section title="Schedule">
                <Grid>
                  <F label="Standup time"><input placeholder="09:00" className={inputCls} value={form.standup_time || ''} onChange={(e) => set({ standup_time: e.target.value })} /></F>
                  <F label="EOD time"><input placeholder="17:00" className={inputCls} value={form.eod_time || ''} onChange={(e) => set({ eod_time: e.target.value })} /></F>
                  <F label="Shift days"><input placeholder="mon,tue,wed,thu,fri" className={inputCls} value={form.shift_days || ''} onChange={(e) => set({ shift_days: e.target.value })} /></F>
                </Grid>
              </Section>
            </Section>
          )}

          {tab === 'contact' && (
            <Section title="Contact Info">
              <F label="Address"><textarea rows={2} className={inputCls} value={form.address || ''} onChange={(e) => set({ address: e.target.value })} /></F>
              <Grid>
                <F label="Emergency Contact Name"><input className={inputCls} value={form.emergency_contact_name || ''} onChange={(e) => set({ emergency_contact_name: e.target.value })} /></F>
                <F label="Emergency Contact Phone"><input className={inputCls} value={form.emergency_contact_phone || ''} onChange={(e) => set({ emergency_contact_phone: e.target.value })} /></F>
              </Grid>
            </Section>
          )}

          <button type="submit" disabled={saveMutation.isPending} className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
            <Save className="h-4 w-4" /> Save
          </button>
        </form>
      ) : (
        <>
          {tab === 'salary' && <CompensationCard userId={user.id} />}
          {tab === 'documents' && <DocumentsCard userId={user.id} />}

          {tab === 'user-info' && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Shield className="h-4 w-4 text-[#EC5B2C]" /> User Information</h2>
              <Grid>
                <F label="Email / Username"><input value={user.email} disabled className={`${inputCls} bg-gray-100 text-gray-500`} /></F>
                <F label="Timezone"><input value={user.timezone} disabled className={`${inputCls} bg-gray-100 text-gray-500`} /></F>
                <F label="Role"><input value={ROLE_LABELS[user.role] ?? customRoles.find((r: RoleDef) => r.key === user.role)?.label ?? user.role} disabled className={`${inputCls} bg-gray-100 text-gray-500`} /></F>
              </Grid>
              <p className="text-xs text-gray-400 mt-3">Role is changed from the Employment Info tab.</p>
            </div>
          )}

          {tab === 'assets' && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Package className="h-4 w-4 text-[#EC5B2C]" /> Assets</h2>
              {items.length === 0 ? (
                <p className="text-sm text-gray-400">No items currently assigned.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {items.map((it) => (
                    <li key={it.id} className="py-2.5 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{it.name}</div>
                        <div className="text-xs text-gray-400 capitalize">{it.category}{it.asset_tag ? ` · ${it.asset_tag}` : ''}</div>
                      </div>
                      <button onClick={() => navigate(`/${orgSlug}/admin/inventory`)} className="text-xs text-[#EC5B2C] hover:underline">Manage</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'attendance' && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Clock className="h-4 w-4 text-[#EC5B2C]" /> Attendance</h2>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">Last 30 days</span>
                  <Link to={`/${orgSlug}/admin/analytics/${user.id}`} className="flex items-center gap-1 text-xs text-[#EC5B2C] hover:underline">
                    <LinkIcon className="h-3 w-3" /> View Full Details
                  </Link>
                </div>
              </div>
              <AttendanceTimeline userId={user.id} />
            </div>
          )}

          {tab === 'leave' && <LeaveBalanceCard userId={user.id} />}
        </>
      )}
    </div>
  )
}

function LeaveBalanceCard({ userId }: { userId: string }) {
  const { data: balances } = useQuery({ queryKey: ['leave-balance', userId], queryFn: () => getUserBalance(userId) })
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 h-fit">
      <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-[#EC5B2C]" /> Leave Balance</h2>
      {!balances ? <p className="text-sm text-gray-400">Loading…</p> : (
        <ul className="space-y-3">
          {(Object.entries(balances) as [string, LeaveBalance][]).map(([type, b]) => (
            <li key={type}>
              <div className="flex justify-between text-sm mb-1"><span className="capitalize text-gray-700">{type}</span><span className="text-gray-500">{b.remaining} / {b.total} left</span></div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[#EC5B2C]" style={{ width: `${b.total > 0 ? (b.used / b.total) * 100 : 0}%` }} /></div>
              <p className="mt-1 text-xs text-gray-500">
                Used {b.used}
                {b.pending > 0 && <span className="ml-1.5 text-amber-600">· {b.pending} pending</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function CompensationCard({ userId }: { userId: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState('')
  const { data: salary } = useQuery({ queryKey: ['salary', userId], queryFn: () => getUserSalary(userId) })
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState<SalaryInput>({ basic: 0, per_hour_rate: 0, house_rent: 0, medical: 0, conveyance: 0, other_allowances: 0, effective_from: today })

  function openEdit() {
    setForm({
      basic: Number(salary?.basic ?? 0),
      per_hour_rate: Number(salary?.per_hour_rate ?? 0),
      house_rent: Number(salary?.house_rent ?? 0),
      medical: Number(salary?.medical ?? 0),
      conveyance: Number(salary?.conveyance ?? 0),
      other_allowances: Number(salary?.other_allowances ?? 0),
      effective_from: salary?.effective_from || today,
      currency: salary?.currency || 'PKR',
    })
    setError(''); setEditing(true)
  }

  const mut = useMutation({
    mutationFn: () => setUserSalary(userId, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['salary', userId] }); setEditing(false) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to save salary'),
  })

  const cur = salary?.currency || 'PKR'
  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Wallet className="h-4 w-4 text-[#EC5B2C]" /> Compensation</h2>
        <button onClick={openEdit} className="text-xs text-[#EC5B2C] hover:underline">{salary ? 'Edit' : 'Set salary'}</button>
      </div>
      {!salary ? (
        <p className="text-sm text-gray-400">No salary set.</p>
      ) : (
        <div>
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Basic" value={money(salary.basic, cur)} tone="gray" />
            <StatTile label="Per Hour" value={money(salary.per_hour_rate, cur)} tone="gray" />
            <StatTile label="Allowances" value={money(salary.allowances_total, cur)} tone="gray" />
            <StatTile label="Gross / month" value={money(salary.gross, cur)} tone="orange" />
          </div>
          <p className="mt-2 text-xs text-gray-400">Effective {salary.effective_from}</p>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-5 py-3">
              <div className="font-semibold text-gray-900">Set Salary</div>
              <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
              {([['basic', 'Basic'], ['per_hour_rate', 'Per Hour Rate'], ['house_rent', 'House Rent'], ['medical', 'Medical'], ['conveyance', 'Conveyance'], ['other_allowances', 'Other Allowances']] as const).map(([k, label]) => (
                <label key={k} className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">{label} (PKR)</span>
                  <input type="number" min={0} className={inp} value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} />
                </label>
              ))}
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Effective from</span>
                <input type="date" className={inp} value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
                <button onClick={() => mut.mutate()} disabled={mut.isPending} className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const DOC_TYPES: DocType[] = ['contract', 'cnic', 'offer_letter', 'policy', 'certificate', 'other']

function DocumentsCard({ userId }: { userId: string }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<DocumentInput>({ name: '', doc_type: 'contract', document_url: '', status: 'received' })
  const { data: docs = [] } = useQuery({ queryKey: ['documents', userId], queryFn: () => listUserDocuments(userId) })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['documents', userId] })
  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'

  const addMut = useMutation({
    mutationFn: () => createDocument(userId, { ...form, document_url: form.document_url || null }),
    onSuccess: () => { invalidate(); setAdding(false); setForm({ name: '', doc_type: 'contract', document_url: '', status: 'received' }) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to add'),
  })
  const delMut = useMutation({ mutationFn: (id: string) => deleteDocument(id), onSuccess: invalidate })

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2"><FileText className="h-4 w-4 text-[#EC5B2C]" /> Documents</h2>
        <button onClick={() => { setError(''); setAdding(true) }} className="text-xs text-[#EC5B2C] hover:underline flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>
      {docs.length === 0 ? (
        <p className="text-sm text-gray-400">No documents.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {docs.map((d) => (
            <li key={d.id} className="py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                {d.document_url ? (
                  <a href={d.document_url} target="_blank" rel="noreferrer" className="text-sm font-medium text-gray-900 hover:text-[#EC5B2C] truncate block">{d.name}</a>
                ) : (
                  <span className="text-sm font-medium text-gray-900 truncate block">{d.name}</span>
                )}
                <span className="text-xs text-gray-400 capitalize">{d.doc_type.replace('_', ' ')} · {d.status}{d.expiry_date ? ` · exp ${d.expiry_date}` : ''}</span>
              </div>
              <button onClick={() => { if (confirm(`Delete "${d.name}"?`)) delMut.mutate(d.id) }} className="p-1 text-gray-300 hover:text-red-500 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAdding(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-5 py-3"><div className="font-semibold text-gray-900">Add Document</div><button onClick={() => setAdding(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button></div>
            <div className="p-5 space-y-3">
              {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
              <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1">Name</span><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
              <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1">Type</span><select className={`${inp} capitalize`} value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value as DocType })}>{DOC_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t.replace('_', ' ')}</option>)}</select></label>
              <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1">Document URL (optional)</span><input className={inp} placeholder="https://…" value={form.document_url || ''} onChange={(e) => setForm({ ...form, document_url: e.target.value })} /></label>
              <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1">Expiry (optional)</span><input type="date" className={inp} value={form.expiry_date || ''} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></label>
              <div className="flex justify-end gap-2 pt-1"><button onClick={() => setAdding(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button><button onClick={() => addMut.mutate()} disabled={!form.name || addMut.isPending} className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60">Add</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  )
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid sm:grid-cols-2 gap-3">{children}</div>
}
function F({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}{hint && <span className="text-gray-400"> ({hint})</span>}</span>
      {children}
    </label>
  )
}
